import { ProtocolIcon } from './chunk-7LGWFA24.js';
import { ProtocolBadge } from './chunk-FQZQKSLM.js';
import { LAYERZERO_V2, LAYERZERO_V2_COMPONENTS, LAYERZERO_V2_EVENTS, pickField, LAYERZERO_V2_FIELDS, LAYERZERO_V2_LABELS, ProtocolValue, SectionCard, FactGrid, ProtocolParamList, ProtocolDisclosure, fieldAtPath } from './chunk-5X2SUL4L.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { BadgeCheck } from 'lucide-react';
import { rawOf } from '@authe/fe-commons/storage';

function LayerZeroCompose({ event, chain, renderAddress }) {
  const delivered = event.name === "ComposeDelivered";
  const address = (value) => typeof value === "string" && renderAddress ? renderAddress(value, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value });
  return /* @__PURE__ */ jsxs(SectionCard, { title: delivered ? "Destination callback receipt" : "Destination action queued", protocolId: LAYERZERO_V2.protocolId, children: [
    /* @__PURE__ */ jsx("p", { className: "mb-3 text-xs text-muted-foreground", children: delivered ? "ComposeDelivered records callback completion. Application receipts show whether it swapped, paid out or refunded." : "ComposeSent queues a later callback. This receipt alone does not establish its execution or a final payout." }),
    /* @__PURE__ */ jsx(FactGrid, { facts: [
      { label: "Queue sender", value: address(pickField(event.params, "from")) },
      { label: "Destination contract", value: address(pickField(event.params, "to")) },
      { label: "Message GUID", value: /* @__PURE__ */ jsx(ProtocolValue, { value: pickField(event.params, "guid") }) },
      { label: "Compose index", value: /* @__PURE__ */ jsx(ProtocolValue, { value: pickField(event.params, "index") }) },
      { label: "Emitting endpoint", value: address(event.address) }
    ] }),
    /* @__PURE__ */ jsxs("details", { className: "mt-3", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground", children: "All event fields" }),
      /* @__PURE__ */ jsx(ProtocolParamList, { params: event.params, emptyLabel: "No decoded fields" })
    ] })
  ] });
}
var QUORUM_CHIP = "inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-2xs font-medium";
function LayerZeroPayloadVerified({
  event,
  hint,
  chain,
  renderAddress
}) {
  const pid = hint.protocolId ?? LAYERZERO_V2.protocolId;
  const dvn = pickField(event.params, LAYERZERO_V2_FIELDS.dvn);
  const confirmations = pickField(event.params, LAYERZERO_V2_FIELDS.confirmations);
  const proofHash = pickField(event.params, LAYERZERO_V2_FIELDS.proofHash);
  const address = (v) => typeof v === "string" && renderAddress ? renderAddress(v, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value: v });
  const facts = [
    { label: LAYERZERO_V2_LABELS.verifier, value: address(dvn), when: dvn },
    // The emitter is the ReceiveUln library that recorded the attestation —
    // a per-chain deployment, which is why this protocol is claimed by topic
    // rather than by address.
    { label: LAYERZERO_V2_LABELS.library, value: address(event.address), when: event.address },
    {
      label: LAYERZERO_V2_LABELS.confirmations,
      value: /* @__PURE__ */ jsx(ProtocolValue, { value: confirmations }),
      when: confirmations != null
    },
    { label: LAYERZERO_V2_LABELS.proofHash, value: /* @__PURE__ */ jsx(ProtocolValue, { value: proofHash }), when: proofHash }
  ];
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: pid, icon: BadgeCheck }),
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? "LayerZero payload verified" })
      ] }),
      protocolId: pid,
      meta: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            className: QUORUM_CHIP,
            title: "One verifier attested. Quorum verification and receiver execution are separate stages.",
            children: LAYERZERO_V2_LABELS.oneOfQuorum
          }
        ),
        /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: pid })
      ] }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: "This is one verifier\u2019s attestation. Quorum verification makes the packet eligible for execution; a destination delivery receipt is needed to establish receiver execution." }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All event fields" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: event.params, emptyLabel: "No decoded fields" }) })
        ] })
      ]
    }
  );
}
var PACKET_EVENT_SIGNATURES = [
  "PacketSent(bytes,bytes,address)",
  "PacketVerified((uint32,bytes32,uint64),address,bytes32)",
  "PacketDelivered((uint32,bytes32,uint64),address)",
  "LzReceiveAlert(address,address,(uint32,bytes32,uint64),bytes32,uint256,uint256,bytes,bytes,bytes)"
];
var PACKET_CALL_SIGNATURES = [
  "execute302((address,(uint32,bytes32,uint64),bytes32,bytes,bytes,uint256))",
  "lzReceive((uint32,bytes32,uint64),address,bytes32,bytes,bytes)",
  "lzReceive((uint32,bytes32,uint64),bytes32,bytes,address,bytes)"
];
var stages = {
  PacketSent: ["Message sent", "The source endpoint emitted a packet. Destination verification and delivery are separate stages."],
  PacketVerified: ["Packet verification committed", "The destination endpoint accepted the verified payload. Execution still requires a successful delivery attempt."],
  PacketDelivered: ["Message delivered", "The destination receiver returned successfully. Token payout and any later compose execution require their own application receipts."],
  LzReceiveAlert: ["Delivery attempt failed", "This attempt failed. A later retry may deliver the same packet; this alert alone does not establish its final outcome."]
};
function Packet({ params, name, address, receipt }) {
  const [title, explanation] = receipt ? stages[name] ?? [name, ""] : ["Delivery request", "These are submitted call arguments. Use committed PacketDelivered receipts to establish message delivery; a call may revert."];
  const get = (field) => fieldAtPath(params, field, `_executionParams.${field}`, `encodedPayload.${field}`);
  const facts = [
    { label: receipt ? "Event" : "Function", value: name },
    { label: receipt ? "Emitter" : "Called contract", value: address },
    { label: "Message GUID", value: get("guid") },
    { label: "Source endpoint ID", value: get("origin.srcEid") ?? get("srcEid") },
    { label: "Channel nonce", value: get("origin.nonce") ?? get("nonce") },
    { label: "Destination receiver (application contract)", value: name === "PacketSent" ? void 0 : get("receiver") },
    { label: "Payload hash", value: get("payloadHash") }
  ].map(({ label, value }) => ({ label, value: /* @__PURE__ */ jsx(ProtocolValue, { value }), when: value != null }));
  const sender = get("origin.sender") ?? get("sender");
  if (sender != null) facts.push({ label: "Source sender (remote address bytes)", value: /* @__PURE__ */ jsx("span", { className: "break-all font-mono text-xs", children: String(rawOf(sender)) }), when: true });
  return /* @__PURE__ */ jsxs(SectionCard, { protocolId: "layerzero_v2", title: `LayerZero \xB7 ${title}`, children: [
    /* @__PURE__ */ jsx("p", { className: "mb-3 text-xs text-muted-foreground", children: explanation }),
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: "Message delivery is not a token amount. Read the application's transfer receipts for value received." }),
    /* @__PURE__ */ jsx(ProtocolDisclosure, { className: "mt-3", summary: receipt ? "All event fields" : "All call arguments", children: /* @__PURE__ */ jsx(ProtocolParamList, { params }) })
  ] });
}
function LayerZeroPacketEvent({ event }) {
  return /* @__PURE__ */ jsx(Packet, { params: event.params, name: event.name ?? "", address: event.address, receipt: true });
}
function LayerZeroPacketCall({ call }) {
  return /* @__PURE__ */ jsx(Packet, { params: call.params, name: call.label ?? "", address: call.address, receipt: false });
}

// src/protocols/layerzero_v2/index.ts
var PID = LAYERZERO_V2.protocolId;
var C = LAYERZERO_V2_COMPONENTS;
var LAYERZERO_V2_HINTS = {
  valueHints: [],
  callHints: PACKET_CALL_SIGNATURES.map((signature) => ({
    protocolId: PID,
    call: signature.slice(0, signature.indexOf("(")),
    signature,
    component: "LayerZeroPacketCall",
    summaryFields: []
  })),
  eventHints: [
    ...PACKET_EVENT_SIGNATURES.map((signature) => ({
      protocolId: PID,
      event: signature.slice(0, signature.indexOf("(")),
      signature,
      component: "LayerZeroPacketEvent",
      summaryFields: []
    })),
    ...["ComposeSent", "ComposeDelivered"].map((event) => ({ protocolId: PID, event, component: C.compose, summaryFields: [] })),
    {
      protocolId: PID,
      event: LAYERZERO_V2_EVENTS.payloadVerified,
      component: C.payloadVerified,
      summaryFields: []
    }
  ]
};
function registerLayerZeroV2(registry) {
  registry.registerCall("LayerZeroPacketCall", LayerZeroPacketCall);
  registry.registerEvent("LayerZeroPacketEvent", LayerZeroPacketEvent);
  registry.registerEvent(C.compose, LayerZeroCompose);
  registry.registerEvent(C.payloadVerified, LayerZeroPayloadVerified);
  registry.registerHints(LAYERZERO_V2_HINTS);
  return registry;
}

export { LAYERZERO_V2_HINTS, LayerZeroPayloadVerified, registerLayerZeroV2 };
//# sourceMappingURL=chunk-NJFDFXPH.js.map
//# sourceMappingURL=chunk-NJFDFXPH.js.map